import type { OrganizationNode } from './organizationTree';

/** What a card offers to create under its entity on hover (spec 6.6). */
export type EntityCreation = 'partner' | 'account' | 'subAccount';

/** The viewer's half of each card button's rule, named by what it adds and under which card. */
export type EntityCreationPermissions = {
  /** `+ Partner` on the organization card: Admin with partner creation. */
  canAddPartnerToOrganization: boolean;
  /** `+ Account` on the organization card: Admin with account creation. */
  canAddAccountToOrganization: boolean;
  /**
   * The same, less an Inside Sales session: `+ Account` on a partner card. `POST /partner/account`
   * is PARTNER auth with ActiveRequired, so the child partner has to be Active too.
   */
  canAddAccountUnderPartner: boolean;
  /** `+ Sub-Account` on an active account card with sub-accounts enabled: Admin, not Inside Sales. */
  canAddSubAccountUnderAccount: boolean;
};

/**
 * The creations a card offers, in the order the buttons sit: the organization offers a partner and
 * an account, a partner an account, an account or sub-account a sub-account — each only when the
 * session may create it there. An empty list keeps the balance on hover.
 */
export default function entityCreationsFor(
  { kind, entity, status }: Pick<OrganizationNode, 'kind' | 'entity' | 'status'>,
  permissions: EntityCreationPermissions,
): EntityCreation[] {
  switch (kind) {
    case 'Organization': {
      const creations: EntityCreation[] = [];

      if (permissions.canAddPartnerToOrganization) creations.push('partner');
      if (permissions.canAddAccountToOrganization) creations.push('account');

      return creations;
    }

    // Inside Sales creates accounts through its own wizard, which cannot act as another partner — the
    // partner page's Accounts tab hides Add Account for the same reason.
    case 'Partner':
      return permissions.canAddAccountUnderPartner && entity.IsActive && entity.PartnerType !== 'Inside Sales'
        ? ['account']
        : [];
    case 'Account':
    case 'Sub-Account':
      return permissions.canAddSubAccountUnderAccount && status === 'Active' && entity.SubAccountsEnabled
        ? ['subAccount']
        : [];
  }
}
